// src/components/NavRailIcon.jsx
//
// Per-item icon renderer for the sidebar (NavRail). Each branch returns
// a single stroke-only SVG using stroke-thickening for the active state
// (stroke-width 1.7 idle → 2.2 active). No filled-polygon active variants.
//
// All icons use currentColor so the parent NavButton's `color` style
// controls both idle (T.navItemMuted) and active (T.hdrFrom) tones.
//
// Adding a new sidebar destination? Add a new `if (itemKey === ...)`
// branch and pick a 24×24 stroke-only SVG that matches the icon set.
//
// ADMIN-NAV-TOGGLES-V2: Added ask_portfolio branch — clean magnifier
// (circle + handle line), visually distinct from the Pulse heartbeat.

import React from "react";
import { ListChecks, Hash, Folder } from "lucide-react";

export function NavRailIcon({ itemKey, filled = false, size = 22 }) {
  const s   = { width: size, height: size, display: "block", flexShrink: 0 };
  const sw  = filled ? 2.2 : 1.7;
  const base = {
    style:           s,
    viewBox:         "0 0 24 24",
    fill:            "none",
    stroke:          "currentColor",
    strokeWidth:     sw,
    strokeLinecap:   "round",
    strokeLinejoin:  "round",
  };

  // Shared connection glyphs only; these do not add sidebar destinations.
  if (itemKey === "slack") return <Hash style={s} strokeWidth={sw} />;
  if (itemKey === "drive") return <Folder style={s} strokeWidth={sw} />;
  if (itemKey === "feed") {
    // Updates deep-links: card with stacked lines, not the Signals pulse.
    return (
      <svg {...base}>
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="M8 9.5h8M8 13h8M8 16.5h5" />
      </svg>
    );
  }

  if (itemKey === "home") {
    // House: peaked roof + walls + centred door cutout
    return (
      <svg {...base}>
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
        <path d="M9 22V12h6v10" />
      </svg>
    );
  }

  if (itemKey === "portfolio" || itemKey === "places") {
    // Offices: two-buildings glyph — matches the mock and ShellV2 Building2 lucide icon.
    return (
      <svg {...base}>
        <path d="M4 20V6l7-3v17M11 20h9V9l-9-3" />
      </svg>
    );
  }

  if (itemKey === "leases") {
    // Leases: document with folded corner and two text lines
    return (
      <svg {...base}>
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    );
  }

  if (itemKey === "signals") {
    // Signals: pulse/radar glyph for monitored portfolio conditions.
    return (
      <svg {...base}>
        <polyline points="3 12 7 12 9.5 5 14.5 19 17 12 21 12" />
      </svg>
    );
  }

  if (itemKey === "plan") {
    // Plan: bullseye target — strategy / direction read
    return (
      <svg {...base}>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="12" cy="12" r="1" />
      </svg>
    );
  }

  if (itemKey === "projects") {
    // Projects: briefcase outline
    return (
      <svg {...base}>
        <rect x="2" y="7" width="20" height="14" rx="2" />
        <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />
        <line x1="12" y1="11" x2="12" y2="17" />
        <line x1="9"  y1="14" x2="15" y2="14" />
      </svg>
    );
  }

  if (itemKey === "okrs") {
    // OKRs: stacked target lines with a completion check.
    return (
      <svg {...base}>
        <path d="M5 4h14v16H5z" />
        <path d="M8 8h8M8 12h5M8 16l2 2 5-5" />
      </svg>
    );
  }

  if (itemKey === "reports") {
    // Reports: bar chart with three vertical bars and a baseline axis
    return (
      <svg {...base}>
        <line x1="4"  y1="20" x2="20" y2="20" />
        <line x1="6"  y1="20" x2="6"  y2="14" />
        <line x1="12" y1="20" x2="12" y2="4"  />
        <line x1="18" y1="20" x2="18" y2="10" />
      </svg>
    );
  }

  if (itemKey === "roadmap") {
    // Roadmap: flagpole + triangular pennant — milestone marker
    return (
      <svg {...base}>
        <line x1="5" y1="3" x2="5" y2="21" />
        <path d="M5 3h13l-4 5 4 5H5" />
      </svg>
    );
  }

  if (itemKey === "radar") {
    // Radar: target crosshair — portfolio-wide tracking and urgency monitoring.
    return (
      <svg {...base}>
        <circle cx="12" cy="12" r="8" />
        <circle cx="12" cy="12" r="3" />
        <line x1="12" y1="2"  x2="12" y2="6"  />
        <line x1="12" y1="18" x2="12" y2="22" />
        <line x1="2"  y1="12" x2="6"  y2="12" />
        <line x1="18" y1="12" x2="22" y2="12" />
      </svg>
    );
  }

  if (itemKey === "playbooks") {
    // Playbooks: open book — action template library.
    return (
      <svg {...base}>
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
      </svg>
    );
  }

  if (itemKey === "decisions") {
    // Decisions: balance / scale glyph — lease renewal decision register (P-15).
    // Kept for admin preview overlay (not in main nav).
    return (
      <svg {...base}>
        <line x1="12" y1="3" x2="12" y2="21" />
        <path d="M5 21h14" />
        <path d="M5 9l-2 5h4l-2-5z" />
        <path d="M19 9l-2 5h4l-2-5z" />
        <line x1="5" y1="9" x2="12" y2="5" />
        <line x1="19" y1="9" x2="12" y2="5" />
      </svg>
    );
  }

  if (itemKey === "reviews") {
    // Reviews: clipboard with a check line — manual check-off register (P-19).
    return (
      <svg {...base}>
        <rect x="5" y="3" width="14" height="18" rx="2" />
        <path d="M9 3a3 3 0 0 1 6 0" />
        <line x1="9" y1="11" x2="15" y2="11" />
        <polyline points="9 15 11 17 15 13" />
      </svg>
    );
  }

  if (itemKey === "workflows") {
    // Workflows: lucide list-checks, matched to the canonical v2 left nav
    // icon (src/v2/Shell.jsx NAV_ICON_MAP) so both places render identically.
    return <ListChecks style={s} strokeWidth={sw} />;
  }

  if (itemKey === "admin") {
    // Admin: gear (settings cog)
    return (
      <svg {...base}>
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    );
  }

  if (itemKey === "ask_portfolio") {
    // Ask Portfolio: magnifier (circle + handle line).
    // Visually distinct from the Pulse heartbeat — clean search icon shape.
    return (
      <svg {...base}>
        <circle cx="10.5" cy="10.5" r="6" />
        <line x1="15" y1="15" x2="21" y2="21" />
      </svg>
    );
  }

  return null;
}
